import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import { auth } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import Message from '../common/Message';
import ChatMessageForm from '../common/ChatMessageForm';
import { useMessageAuthors } from '../../hooks/useMessageAuthors';
import { useLastReadModule } from '../../context/LastReadContext';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { toMillis } from '../../platform/lastRead/lastReadUtils';
import {
  deleteChatMessage,
  isChatAdmin,
  loadOlderMessages,
  markChatRoomAsRead,
  subscribeToMessages,
} from '../../services/chatService';
import './ChatRoom.css';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';

function ChatRoom({ unitId }) {
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const { user: legacyUser } = useUser();
  const { lastReadMap } = useLastReadModule(LAST_READ_MODULES.CHAT);
  const [messages, setMessages] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const initialLoadRef = useRef(true);
  const shouldStickToBottomRef = useRef(true);

  const isAdmin = isChatAdmin({ memberships, tenantId, legacyUser });
  const authorsByUid = useMessageAuthors(messages);
  const lastReadTimestamp = toMillis(lastReadMap[unitId]) || 0;

  const scrollToBottom = (behavior = 'auto') => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
      });
    });
  };

  useEffect(() => {
    initialLoadRef.current = true;
    shouldStickToBottomRef.current = true;
  }, [unitId]);

  useEffect(() => {
    if (!unitId || !tenantId) return undefined;

    const unsubscribe = subscribeToMessages({
      tenantId,
      unitId,
      onData: ({ messages: loadedMessages, lastVisible: latestVisible }) => {
        setLoadError('');
        setMessages(loadedMessages);
        setLastVisible(latestVisible);
      },
      onError: (error) => {
        console.error('ChatRoom messages listener error:', error);
        setLoadError(error.message || 'Napaka pri nalaganju sporočil. Poskusite znova.');
      },
    });

    return () => unsubscribe();
  }, [unitId, tenantId]);

  const loadMoreMessages = async () => {
    if (!lastVisible || loadingMore || !tenantId) return;

    setLoadingMore(true);
    const previousScrollHeight = messagesContainerRef.current.scrollHeight;

    try {
      const { messages: moreMessages, lastVisible: nextVisible } = await loadOlderMessages({
        tenantId,
        unitId,
        lastVisible,
      });

      setMessages((prevMessages) => [...moreMessages, ...prevMessages]);
      setLastVisible(nextVisible);
    } finally {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop =
            messagesContainerRef.current.scrollHeight - previousScrollHeight;
        }
        setLoadingMore(false);
      }, 0);
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 96;

    if (container.scrollTop === 0) {
      loadMoreMessages();
    }
  };

  const confirm = useConfirm();
  const toast = useToast();

  const handleDeleteMessage = async (messageId) => {
    const confirmed = await confirm({
      title: 'Izbriši sporočilo',
      message: 'Ali ste prepričani, da želite izbrisati to sporočilo?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteChatMessage(messageId);
      toast.deleted('Sporočilo je bilo izbrisano.');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Sporočila ni bilo mogoče izbrisati.');
    }
  };

  useEffect(() => {
    if (messages.length === 0) return undefined;

    if (initialLoadRef.current || shouldStickToBottomRef.current) {
      scrollToBottom('auto');
      initialLoadRef.current = false;
    }

    const timer = setTimeout(() => {
      markChatRoomAsRead(auth.currentUser.uid, unitId);
    }, 500);
    return () => clearTimeout(timer);
  }, [messages, unitId]);

  useEffect(() => {
    if (messages.length === 0) return;
    if (shouldStickToBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [authorsByUid, messages.length]);

  let dividerShown = false;

  return (
    <Card className="chat-card platform-content-detail-card">
      <Card.Body
        className="chat-card-body"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loadError && (
          <div className="alert alert-warning mb-2" role="alert">
            {loadError}
          </div>
        )}
        {!loadError && messages.length === 0 && (
          <p className="text-muted mb-0">Ni sporočil v tej klepetalnici.</p>
        )}
        {messages.map((msg, index) => {
          const showDivider =
            !dividerShown
            && msg.createdAt
            && msg.createdAt > lastReadTimestamp
            && msg.uid !== auth.currentUser.uid;
          if (showDivider) {
            dividerShown = true;
          }
          const previousMessage = messages[index - 1];
          const nextMessage = messages[index + 1];
          const isOtherUser = msg.uid !== auth.currentUser?.uid;
          const showAuthorName =
            isOtherUser
            && (!previousMessage || previousMessage.uid !== msg.uid);
          const showAuthorAvatar =
            isOtherUser
            && (!nextMessage || nextMessage.uid !== msg.uid);
          const isGroupedWithPrevious =
            Boolean(previousMessage && previousMessage.uid === msg.uid);
          const isGroupedWithNext =
            Boolean(nextMessage && nextMessage.uid === msg.uid);
          const authorMeta = authorsByUid[msg.uid] || {};

          return (
            <React.Fragment key={msg.id}>
              {showDivider && (
                <div className="unread-divider">
                  Neprebrana sporočila
                </div>
              )}
              <Message
                message={msg}
                isAdmin={isAdmin}
                showAuthorName={showAuthorName}
                showAuthorAvatar={showAuthorAvatar}
                isGroupedWithPrevious={isGroupedWithPrevious}
                isGroupedWithNext={isGroupedWithNext}
                authorPhotoURL={authorMeta.photoURL}
                authorColor={authorMeta.chatColor}
                authorDisplayName={authorMeta.displayName}
                onDelete={() => handleDeleteMessage(msg.id)}
              />
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </Card.Body>
      <Card.Footer className="p-3 chat-card-footer">
        <ChatMessageForm tenantId={tenantId} unit={unitId} />
      </Card.Footer>
    </Card>
  );
}

export default ChatRoom;
